import React, { useState } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import {
  Paper,
  Divider,
  Box,
  Typography,
  makeStyles,
  Button,
  useTheme
} from "@material-ui/core";
import { useParams } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  Loader,
  IntroText,
  LogoGGT,
  PatientDetails,
  LogoGGV,
  VaxYesTeaser
} from "../../components";
import { GET_TEST_RESULT } from "../../queries";
import { AppointmentAuthForm } from "./children";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils";

const useClasses = makeStyles({
  resultText: {
    fontSize: "3rem",
    textTransform: "uppercase",
    fontWeight: "700"
  },
  resultSummary: {
    fontWeight: "700",
    textDecoration: "underline"
  }
});

const PaperWrapper = ({ children }) => {
  return (
    <Box mb={2}>
      <Paper>
        <Box p={2} textAlign="center">
          {children}
        </Box>
      </Paper>
    </Box>
  );
};

const AppointmentResult = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { token } = useParams();
  const [dobMatch, setDobMatch] = useState(false);
  const [testResult, setTestResult] = useState();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [getTestResult, { error, loading }] = useLazyQuery(GET_TEST_RESULT, {
    onCompleted: ({ testResult }) => {
      if (testResult && testResult.status === "success") {
        setDobMatch(true);
        setTestResult(testResult);
      } else {
        setDobMatch(false);
        enqueueSnackbar(t("appointment.result.error_dob_match"), {
          variant: "error"
        });
      }
    }
  });

  const handleSubmitAuth = ({ dob = "" }) => {
    getTestResult({
      variables: {
        token,
        dob: formatDate(dob, "YYYY-MM-DD", "YYYYMMDD")
      }
    });
  };

  const getResultColor = result => {
    switch (result.toLowerCase()) {
      case "positive":
        return theme.palette.error.main;
      case "negative":
        return theme.palette.primary.main;
      default:
        return theme.palette.common.black;
    }
  };

  if (error)
    return (
      <Typography color="error">{t("appointment.result.error_api")}</Typography>
    );
  if (loading) return <Loader />;

  return (
    <>
      <Sheet data-cy="schedule-test-result">
        <SheetContent>
          <Box textAlign="center" pb={2}>
            <LogoGGT
              textColor={theme.palette.primary.main}
              bgColor={theme.palette.primary.main}
            />
          </Box>
          {dobMatch ? (
            <>
              {testResult && testResult.result ? (
                <>
                  <PaperWrapper>
                    {(testResult.patient_name || testResult.patient_dob) && (
                      <PatientDetails
                        name={testResult.patient_name}
                        dob={testResult.patient_dob}
                      />
                    )}
                    <Typography>
                      {t("appointment.result.summary_title")}
                    </Typography>
                    <Typography
                      data-cy="test-result-detail"
                      className={classes.resultText}
                      style={{
                        color: getResultColor(testResult.result)
                      }}
                    >
                      {testResult.result}
                    </Typography>
                    {testResult.result.toLowerCase() === "positive" && (
                      <Typography paragraph>
                        {t("appointment.result.summary_subtitle")}{" "}
                        <Box className={classes.resultSummary}>
                          {t("appointment.result.positive_text")}
                        </Box>
                      </Typography>
                    )}
                    {testResult.lab_report_url && (
                      <Box mb={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          href={testResult.lab_report_url}
                          rel="noopener"
                          target="_blank"
                        >
                          {t("appointment.result.button_lab_report")}
                        </Button>
                      </Box>
                    )}
                    {testResult.result.toLowerCase() === "positive" && (
                      <Typography paragraph>
                        {t("appointment.result.positive_next_step")}
                      </Typography>
                    )}
                    {testResult.result.toLowerCase() === "negative" && (
                      <Typography paragraph>
                        {t("appointment.result.negative_next_step")}
                      </Typography>
                    )}
                  </PaperWrapper>
                  <PaperWrapper>
                    <VaxYesTeaser />
                  </PaperWrapper>
                  <PaperWrapper>
                    <Typography variant="body2" paragraph>
                      <strong>{t("appointment.result.faqs_title")}</strong>
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      color="default"
                      href="https://www.gogettested.com/confirmationv1"
                      rel="noopener"
                      target="_blank"
                    >
                      {t("appointment.result.button_view_faqs")}
                    </Button>
                  </PaperWrapper>
                </>
              ) : (
                <PaperWrapper>
                  <Typography color="error">
                    {t("appointment.result.error_empty")}
                  </Typography>
                </PaperWrapper>
              )}
            </>
          ) : (
            <Box mb={2}>
              <Paper>
                <Box p={2} textAlign="center">
                  <IntroText>{t("appointment.result.dob_confirm")}</IntroText>
                  <AppointmentAuthForm
                    onSubmit={handleSubmitAuth}
                    formSubmitting={loading}
                  />
                </Box>
              </Paper>
            </Box>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export { AppointmentResult };
